import React, { useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePromises = new Map();

const getStripePromise = (publishableKey) => {
  if (!stripePromises.has(publishableKey)) {
    stripePromises.set(publishableKey, loadStripe(publishableKey));
  }
  return stripePromises.get(publishableKey);
};

const formatAmount = (amountCents, currency) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
    }).format(Number(amountCents || 0) / 100);
  } catch {
    return `${(Number(amountCents || 0) / 100).toFixed(2)} ${String(currency || 'usd').toUpperCase()}`;
  }
};

const StripeCheckoutForm = ({ amountCents, currency, returnUrl, onPaymentSubmitted, paymentLabel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submittedStatus, setSubmittedStatus] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || isSubmitting) return;

    setErrorMessage('');
    setSubmittedStatus('');
    setIsSubmitting(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Stripe could not confirm this payment. Try another payment method.');
        return;
      }

      const nextStatus = paymentIntent?.status || 'processing';
      setSubmittedStatus(nextStatus);
      await onPaymentSubmitted?.({
        paymentIntentId: paymentIntent?.id || '',
        status: nextStatus,
      });
    } catch {
      setErrorMessage('Stripe could not confirm this payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" aria-label={`Secure ${paymentLabel} payment`}>
      <PaymentElement options={{ layout: 'tabs' }} />

      {errorMessage ? (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </p>
      ) : null}

      {submittedStatus ? (
        <p role="status" className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">
          Payment submitted ({submittedStatus}). Booking status updates after Stripe's signed webhook is processed.
        </p>
      ) : null}

      <button
        type="submit"
        disabled={!stripe || !elements || isSubmitting || Boolean(submittedStatus)}
        className="w-full rounded-lg bg-[#304A8A] px-4 py-2 font-semibold text-white hover:bg-[#263C70] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? 'Confirming securely…' : `Pay ${formatAmount(amountCents, currency)} ${paymentLabel}`}
      </button>

      <p className="text-xs text-gray-600">
        Card and payment details are collected securely by Stripe and never pass through Scheduled Beauty's servers.
      </p>
    </form>
  );
};

const StripePaymentForm = ({
  publishableKey,
  clientSecret,
  bookingId,
  amountCents,
  currency,
  onPaymentSubmitted,
  paymentType = 'deposit',
}) => {
  const stripePromise = useMemo(
    () => (publishableKey ? getStripePromise(publishableKey) : null),
    [publishableKey],
  );
  const returnUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/myBookings?payment_return=1&booking_id=${encodeURIComponent(bookingId)}&payment_type=${encodeURIComponent(paymentType)}`;
  }, [bookingId, paymentType]);
  const paymentLabel = paymentType === 'tip'
    ? 'tip'
    : paymentType === 'final'
      ? 'balance'
      : 'deposit';
  const options = useMemo(() => ({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#6D3B7E',
        borderRadius: '8px',
      },
    },
  }), [clientSecret]);

  if (!publishableKey || !clientSecret) {
    return (
      <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        Secure checkout is unavailable because Stripe configuration is incomplete.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {publishableKey.startsWith('pk_test_') ? (
        <p className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
          Stripe test mode — no live charge
        </p>
      ) : null}
      <Elements key={clientSecret} stripe={stripePromise} options={options}>
        <StripeCheckoutForm
          amountCents={amountCents}
          currency={currency}
          returnUrl={returnUrl}
          onPaymentSubmitted={onPaymentSubmitted}
          paymentLabel={paymentLabel}
        />
      </Elements>
    </div>
  );
};

export default StripePaymentForm;
